// Controls an Ultimate (C64 Ultimate, Ultimate 64, Ultimate-II+ ...) through its REST API, for the setup. The page
// cannot call the API itself (no CORS headers; an https page may not call a plain http address), so the device
// does it over its Wi-Fi: see firmware/main/ubridge.c. Used by the bus pin wizard to run the pin finder program
// and to reset the C64 without any help from the user.
import { UB } from './device.js';
import { assetUrl } from '../core/assets.js';

export class UltimateError extends Error {
  // kind: 'wifi' (the device could not join the network), 'host' (no answer from the address), 'password',
  // 'http' (another status), 'device' (the device refused a command)
  constructor(kind, message) {
    super(message);
    this.kind = kind;
  }
}

// product and firmware from the start of the answer to /v1/info (the device only hands over the first 80 bytes)
export function parseInfo(text) {
  const product = /"product"\s*:\s*"([^"]*)"/.exec(text)?.[1] ?? null;
  const firmware = /"firmware_version"\s*:\s*"([^"]*)"/.exec(text)?.[1] ?? null;
  return product ? { product, firmware } : null;
}

const hex = (bytes) => [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');

// What the web interface's "upload and run" does for a BASIC program (the capture in the repository):
// the program (without its two load address bytes) to $0801, the end of the program into $2D/$2E, then RUN
// typed into the keyboard buffer. prg: the bytes of a .prg file that starts at $0801.
export function runBasicRequests(prg) {
  if (prg.length < 4 || prg[0] !== 0x01 || prg[1] !== 0x08) throw new Error('not a BASIC program (a .prg that loads at $0801)');
  const body = prg.subarray(2);
  const end = 0x0801 + body.length;
  return [
    { method: UB.POST, path: '/v1/machine:writemem?address=0801', body },
    { method: UB.PUT, path: `/v1/machine:writemem?address=002d&data=${hex([end & 255, end >> 8])}` },
    { method: UB.PUT, path: '/v1/machine:writemem?address=0277&data=52554E0D' }, // RUN, Enter
    { method: UB.PUT, path: '/v1/machine:writemem?address=00C6&data=04' }, // 4 characters in the keyboard buffer
  ];
}

export class UltimateBridge {
  // getDevice(): the current DeviceLink. sleep(ms) is injectable for tests.
  constructor(getDevice, { sleep = (ms) => new Promise((r) => setTimeout(r, ms)), pollMs = 250 } = {}) {
    this.getDevice = getDevice;
    this.sleep = sleep;
    this.pollMs = pollMs;
  }

  // Sets the address and password and brings the radio up and onto the network. Resolves when requests can be sent.
  async connect({ host, password = '' }, { timeoutMs = 60000 } = {}) {
    const d = this.getDevice();
    if ((await d.ubConfig(host, password)) !== 0) throw new UltimateError('device', 'the device did not accept the address');
    const t0 = Date.now();
    let opened = false;
    // refused while a Wi-Fi printing session still uses the radio (a few seconds after boot): ask again
    while (!opened) {
      opened = (await d.ubOpen()) === 0;
      if (!opened) {
        if (Date.now() - t0 > 30000) throw new UltimateError('wifi', 'The device could not start Wi-Fi: no network is stored in it, or it is busy.');
        await this.sleep(1000);
      }
    }
    for (;;) {
      const s = await d.ubStatus();
      if (s?.state === UB.READY) return;
      if (s?.state === UB.FAILED || s?.state === UB.CLOSED) throw new UltimateError('wifi', 'The device could not join the Wi-Fi network (name or password changed? out of range?).');
      if (Date.now() - t0 > timeoutMs) throw new UltimateError('wifi', 'The device did not get onto the Wi-Fi network in time.');
      await this.sleep(this.pollMs);
    }
  }

  // Joins the network unless the session is open already (the wizard keeps one session for the whole pin search; a
  // session the device closed by itself, or lost in a restart, is opened again).
  async ensureOpen(settings) {
    const status = await this.getDevice().ubStatus().catch(() => null);
    if (status && (status.state === UB.READY || status.state === UB.DONE)) return;
    await this.connect(settings);
  }

  // One request on the open session. Resolves with {http, text}; throws UltimateError for no answer and for 403.
  async request(method, path, body = null) {
    const d = this.getDevice();
    if (body?.length) {
      if ((await d.ubBody(body)) !== 0) throw new UltimateError('device', 'the device did not accept the data');
    }
    if ((await d.ubRequest(method, path, body?.length ?? 0)) !== 0) throw new UltimateError('device', 'the device did not accept the request');
    const t0 = Date.now();
    for (;;) {
      const s = await d.ubStatus();
      if (s?.state === UB.DONE) {
        if (s.error === 2 || s.http === 0) throw new UltimateError('host', 'No answer from that address. Is the Ultimate on, on the same network, with its network service (web) enabled?');
        if (s.http === 403) throw new UltimateError('password', 'The Ultimate refused the password.');
        if (s.http >= 400) throw new UltimateError('http', `The Ultimate answered HTTP ${s.http}.`);
        return { http: s.http, text: s.body };
      }
      if (s && s.state !== UB.BUSY && s.state !== UB.READY) throw new UltimateError('wifi', 'The connection of the device to the network was lost.');
      if (Date.now() - t0 > 20000) throw new UltimateError('host', 'The Ultimate did not answer in time.');
      await this.sleep(this.pollMs);
    }
  }

  // The Ultimate's identity: {product, firmware}. Proves the address, the password and the Wi-Fi path at once.
  async info() {
    const { text } = await this.request(UB.GET, '/v1/info');
    const info = parseInfo(text);
    if (!info) throw new UltimateError('http', 'That address answers, but it does not look like an Ultimate.');
    return info;
  }

  async reset() {
    await this.request(UB.PUT, '/v1/machine:reset');
  }

  async runBasic(prg) {
    for (const r of runBasicRequests(prg)) await this.request(r.method, r.path, r.body ?? null);
  }

  // Radio off; resolves once the device says it is (counting edges may go on after this).
  async close() {
    const d = this.getDevice();
    await d.ubClose();
    for (let i = 0; i < 60; i++) {
      const s = await d.ubStatus().catch(() => null);
      if (s?.state === UB.CLOSED) return;
      await this.sleep(this.pollMs);
    }
    throw new UltimateError('wifi', 'The device did not switch its radio off.');
  }
}

// the steps the dialog lists (the strings the automation reports)
export const PREPARE_STEPS = ['Joining the network', 'Resetting the C64', 'Starting the test program'];
export const RESET_STEPS = ['Resetting the C64'];

export const IECFIND_AUTO_PRG_URL = assetUrl('samples/iecfind-auto.prg');
export async function loadIecfindAuto(fetchFn = fetch) {
  const res = await fetchFn(IECFIND_AUTO_PRG_URL);
  if (!res.ok) throw new Error(`the test program could not be loaded (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

// The steps of the automated pin finding, for the wizard (it decides when to count).
export class IecfindAutomation {
  constructor(bridge, settings, { prg = null, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
    this.bridge = bridge;
    this.settings = settings; // {host, password}
    this.prg = prg;
    this.sleep = sleep;
  }

  // Resets the C64, puts the pin finder into it and starts it (it counts down 5 seconds on the C64's screen before it begins, while the page starts counting). The Wi-Fi
  // session stays open afterwards (the reset step needs it again; the wizard closes it when it ends); it is closed
  // here only when something fails.
  async prepare(onStep = () => {}) {
    const prg = this.prg ?? (this.prg = await loadIecfindAuto());
    try {
      onStep('Joining the network');
      await this.bridge.ensureOpen(this.settings);
      onStep('Resetting the C64');
      await this.bridge.reset();
      await this.sleep(3500); // the C64 boots to READY
      onStep('Starting the test program');
      await this.bridge.runBasic(prg);
    } catch (e) {
      await this.bridge.close().catch(() => {});
      throw e;
    }
  }

  // The session is open (joins if it is not).
  open() {
    return this.bridge.ensureOpen(this.settings);
  }

  reset() {
    return this.bridge.reset();
  }

  close() {
    return this.bridge.close();
  }
}
